import { useState, useEffect } from "react";
import axios from "axios";
import { 
  ListChecks, 
  ExternalLink, 
  Clock, 
  CheckCircle2, 
  Plus, 
  Edit2, 
  Trash2,
  X,
  History,
  RotateCcw
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChecklistSection({ countryCode }) {
  const [checklist, setChecklist] = useState([]);
  const [changelog, setChangelog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    step: 1,
    title: "",
    description: "",
    url: "",
    deadline: "",
    options: [],
    materials: [],
    items: []
  });
  const [newOption, setNewOption] = useState("");
  const [newMaterial, setNewMaterial] = useState("");
  const [newItem, setNewItem] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/checklist/${countryCode}`);
        setChecklist(response.data || []);
      } catch (error) {
        console.error("Error fetching checklist:", error);
        setChecklist([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (countryCode) {
      fetchData();
    }
  }, [countryCode]);

  const fetchChecklist = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/checklist/${countryCode}`);
      setChecklist(response.data || []);
    } catch (error) {
      console.error("Error fetching checklist:", error);
      setChecklist([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchChangelog = async () => {
    try {
      const response = await axios.get(`${API}/checklist/${countryCode}/changelog`);
      setChangelog(response.data || []);
    } catch (error) {
      console.error("Error fetching changelog:", error);
      setChangelog([]);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getChangeTypeStyle = (type) => {
    switch(type) {
      case 'add': return 'bg-green-100 text-green-700';
      case 'update': return 'bg-blue-100 text-blue-700';
      case 'delete': return 'bg-red-100 text-red-700';
      case 'reset': return 'bg-amber-100 text-amber-700';
      default: return 'bg-zinc-100 text-zinc-700';
    }
  };

  const resetForm = () => {
    const maxStep = checklist.length > 0 
      ? Math.max(...checklist.map(c => c.step)) + 1 
      : 1;
    setFormData({
      step: maxStep,
      title: "",
      description: "",
      url: "",
      deadline: "",
      options: [],
      materials: [],
      items: []
    });
    setEditingItem(null);
    setNewOption("");
    setNewMaterial("");
    setNewItem("");
  };

  const handleAdd = () => {
    resetForm();
    setShowDialog(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      step: item.step,
      title: item.title,
      description: item.description,
      url: item.url || "",
      deadline: item.deadline || "",
      options: item.options || [],
      materials: item.materials || [],
      items: item.items || []
    });
    setShowDialog(true);
  };

  const handleDelete = async (item) => {
    const isBaseOverride = !item.is_custom || item.overrides_base_step;
    const confirmMessage = item.is_custom && !item.overrides_base_step
      ? `Delete step "${item.title}"?`
      : `Reset "${item.title}" to original?`;
    
    if (!window.confirm(confirmMessage)) return;

    try {
      const url = item.id.startsWith("base_") 
        ? `${API}/checklist/${item.id}?country_code=${countryCode}`
        : `${API}/checklist/${item.id}`;
      await axios.delete(url);
      toast.success(isBaseOverride ? "Step reset to original" : "Step deleted");
      fetchChecklist();
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error("Failed to delete step");
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.description) {
      toast.error("Title and description are required");
      return;
    }

    try {
      if (editingItem) {
        // For base items, we need to pass country_code as query param
        const isBaseItem = editingItem.id.startsWith("base_");
        const url = isBaseItem 
          ? `${API}/checklist/${editingItem.id}?country_code=${countryCode}`
          : `${API}/checklist/${editingItem.id}`;
        await axios.put(url, formData);
        toast.success(isBaseItem ? "Step customized" : "Step updated");
      } else {
        await axios.post(`${API}/checklist/`, {
          ...formData,
          country_code: countryCode
        });
        toast.success("Step added");
      }
      setShowDialog(false);
      fetchChecklist();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error(error.response?.data?.detail || "Failed to save step");
    }
  };

  const handleToggleComplete = async (item) => {
    try {
      await axios.post(`${API}/checklist/${item.id}/toggle?country_code=${countryCode}&completed=${!item.completed}`);
      setChecklist(prev => prev.map(c => 
        c.id === item.id ? { ...c, completed: !c.completed } : c
      ));
    } catch (error) {
      console.error("Error toggling completion:", error);
      toast.error("Failed to update status");
    }
  };

  const addArrayItem = (field, value, setter) => {
    if (!value.trim()) return;
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], value.trim()]
    }));
    setter("");
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="loading-spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  if (checklist.length === 0) {
    return (
      <div className="card p-8 text-center">
        <ListChecks className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
        <h3 className="text-sm font-medium text-zinc-700 mb-1">No Checklist Items</h3>
        <p className="text-xs text-zinc-500 mb-4">
          Add custom registration steps for {countryCode}
        </p>
        <Button size="sm" onClick={handleAdd} data-testid="add-first-step">
          <Plus className="w-4 h-4 mr-1.5" />
          Add First Step
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-zinc-800 flex items-center gap-2 text-sm">
          <ListChecks className="w-4 h-4 text-violet-600" />
          Registration Checklist ({checklist.length} steps)
        </h3>
        <div className="flex items-center gap-2">
          {/* Changelog Sheet */}
          <Sheet onOpenChange={(open) => open && fetchChangelog()}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" data-testid="checklist-changelog-btn">
                <History className="w-4 h-4 mr-1.5" />
                History
              </Button>
            </SheetTrigger>
            <SheetContent className="w-[400px]">
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Checklist Change History
                </SheetTitle>
              </SheetHeader>
              <ScrollArea className="h-[calc(100vh-100px)] mt-4 pr-4">
                {changelog.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    <History className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No changes recorded yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {changelog.map((change) => (
                      <div 
                        key={change.id} 
                        className="p-3 border rounded-sm bg-zinc-50"
                        data-testid={`changelog-entry-${change.id}`}
                      >
                        <div className="flex items-start justify-between mb-1">
                          <Badge className={`text-xs ${getChangeTypeStyle(change.change_type)}`}>
                            {change.change_type}
                          </Badge>
                          <span className="text-[10px] text-zinc-400">
                            {formatDate(change.timestamp)}
                          </span>
                        </div>
                        <p className="text-xs font-medium text-zinc-700 mb-1">
                          Step {change.step}: {change.notes}
                        </p>
                        {change.old_value && (
                          <div className="text-[10px] text-zinc-500">
                            <span className="font-medium">From:</span> {change.old_value.title || JSON.stringify(change.old_value)}
                          </div>
                        )}
                        {change.new_value && (
                          <div className="text-[10px] text-zinc-500">
                            <span className="font-medium">To:</span> {change.new_value.title || JSON.stringify(change.new_value)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </SheetContent>
          </Sheet>
          
          <Button size="sm" onClick={handleAdd} data-testid="add-checklist-step">
            <Plus className="w-4 h-4 mr-1.5" />
            Add Step
          </Button>
        </div>
      </div>

      {/* Checklist Items */}
      <div className="space-y-2">
        {checklist.map((step) => (
          <div 
            key={step.id}
            className={`card p-3 ${step.completed ? 'bg-green-50 border-green-200' : ''} ${step.is_custom && !step.overrides_base_step ? 'border-violet-200' : ''}`}
            data-testid={`checklist-step-${step.step}`}
          >
            <div className="flex gap-3">
              {/* Completion Checkbox */}
              <div className="flex items-start pt-0.5">
                <Checkbox 
                  checked={step.completed}
                  onCheckedChange={() => handleToggleComplete(step)}
                  data-testid={`toggle-step-${step.id}`}
                />
              </div>

              {/* Step Number */}
              <div className={`w-6 h-6 rounded-full text-white flex items-center justify-center font-bold flex-shrink-0 text-xs ${step.is_custom && !step.overrides_base_step ? 'bg-violet-500' : 'bg-violet-600'}`}>
                {step.step}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <h4 className={`font-medium text-zinc-800 text-sm ${step.completed ? 'line-through text-zinc-500' : ''}`}>
                    {step.title}
                    {step.is_custom && !step.overrides_base_step && (
                      <Badge variant="outline" className="ml-2 text-[10px] border-violet-300 text-violet-600">Custom</Badge>
                    )}
                    {step.is_custom && step.overrides_base_step && (
                      <Badge variant="outline" className="ml-2 text-[10px] border-amber-300 text-amber-600">Modified</Badge>
                    )}
                  </h4>
                  <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 w-6 p-0"
                      onClick={() => handleEdit(step)}
                      title={step.is_custom ? "Edit" : "Customize"}
                      data-testid={`edit-step-${step.id}`}
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    {(step.is_custom || step.overrides_base_step) && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0 text-red-500"
                        onClick={() => handleDelete(step)}
                        title={step.overrides_base_step ? "Reset to original" : "Delete"}
                        data-testid={`delete-step-${step.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-zinc-600 mt-0.5">{step.description}</p>
                
                {step.url && (
                  <a 
                    href={step.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-violet-600 hover:underline flex items-center gap-1 mt-1.5"
                  >
                    Go to registration <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                
                {step.options && step.options.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {step.options.map((opt, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">{opt}</Badge>
                    ))}
                  </div>
                )}
                
                {step.materials && step.materials.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {step.materials.map((mat, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">{mat}</Badge>
                    ))}
                  </div>
                )}

                {step.items && step.items.length > 0 && (
                  <ul className="mt-1.5 space-y-0.5">
                    {step.items.map((item, idx) => (
                      <li key={idx} className="text-xs text-zinc-600 flex items-start gap-1.5">
                        <CheckCircle2 className="w-3 h-3 text-violet-500 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
                
                {step.deadline && (
                  <div className="flex items-center gap-1 text-xs text-amber-600 mt-1.5">
                    <Clock className="w-3 h-3" />
                    Deadline: {step.deadline}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Step' : 'Add New Step'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="step">Step #</Label>
                <Input
                  id="step"
                  type="number"
                  min="1"
                  value={formData.step}
                  onChange={(e) => setFormData({...formData, step: parseInt(e.target.value) || 1})}
                  data-testid="step-number-input"
                />
              </div>
              <div className="col-span-3 space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., Register with LUCID"
                  data-testid="step-title-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe what needs to be done..."
                rows={2}
                data-testid="step-description-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="url">URL</Label>
                <Input
                  id="url"
                  value={formData.url}
                  onChange={(e) => setFormData({...formData, url: e.target.value})}
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deadline">Deadline</Label>
                <Input
                  id="deadline"
                  value={formData.deadline}
                  onChange={(e) => setFormData({...formData, deadline: e.target.value})}
                  placeholder="e.g., March 31, 2026"
                />
              </div>
            </div>

            {/* Options */}
            <div className="space-y-2">
              <Label>Options</Label>
              <div className="flex gap-2">
                <Input
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  placeholder="Add option..."
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('options', newOption, setNewOption))}
                />
                <Button type="button" size="sm" onClick={() => addArrayItem('options', newOption, setNewOption)}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {formData.options.map((opt, idx) => (
                  <Badge key={idx} variant="outline" className="pr-1">
                    {opt}
                    <button onClick={() => removeArrayItem('options', idx)} className="ml-1 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Materials */}
            <div className="space-y-2">
              <Label>Materials/Categories</Label>
              <div className="flex gap-2">
                <Input
                  value={newMaterial}
                  onChange={(e) => setNewMaterial(e.target.value)}
                  placeholder="Add material..."
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('materials', newMaterial, setNewMaterial))}
                />
                <Button type="button" size="sm" onClick={() => addArrayItem('materials', newMaterial, setNewMaterial)}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {formData.materials.map((mat, idx) => (
                  <Badge key={idx} variant="secondary" className="pr-1">
                    {mat}
                    <button onClick={() => removeArrayItem('materials', idx)} className="ml-1 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Sub-items */}
            <div className="space-y-2">
              <Label>Sub-items / Requirements</Label>
              <div className="flex gap-2">
                <Input
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  placeholder="Add sub-item..."
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem('items', newItem, setNewItem))}
                />
                <Button type="button" size="sm" onClick={() => addArrayItem('items', newItem, setNewItem)}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <ul className="space-y-1">
                {formData.items.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-zinc-700">
                    <CheckCircle2 className="w-3.5 h-3.5 text-violet-500" />
                    {item}
                    <button onClick={() => removeArrayItem('items', idx)} className="ml-auto hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} data-testid="save-step-btn">
              {editingItem ? 'Update Step' : 'Add Step'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
